import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Input from './Input';
import { clientApiService } from '../../utils/clientApiService';

const GlobalSearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim() === '') {
      setFilteredResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Debounce search
    debounceRef.current = setTimeout(async () => {
      try {
        // Use the clientApiService to search clients (async, from backend)
        const results = await clientApiService.searchClients(query);
        setFilteredResults(results);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching clients:', error);
        setFilteredResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleResultClick = (client) => {
    setSearchQuery(client.name);
    setShowResults(false);
    // Navigate directly to client detail page
    navigate(`/client-detail/${client.id}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const getBalanceColor = (balance) => {
    if (balance === 0) return 'text-muted-foreground';
    if (balance < 50) return 'text-warning';
    return 'text-success';
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative">
          <Icon 
            name="Search" 
            size={18} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
          />
          <Input
            type="search"
            placeholder="Search clients by name or phone..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => searchQuery && setShowResults(true)}
            className="pl-10 pr-10 w-full transition-all duration-150 focus:ring-2 focus:ring-primary/20"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          )}
          {searchQuery && !isSearching && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setFilteredResults([]);
                setShowResults(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto animate-fade-in">
          {filteredResults.length > 0 ? (
            <div className="py-2">
              {filteredResults.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleResultClick(client)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-150 focus:bg-muted focus:outline-none"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-foreground">
                        {client.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {client.phone ? client.phone.replace(/\D/g, '') : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-mono font-medium ${getBalanceColor(client.balance)}`}>
                        {client.balance} min
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Balance
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <Icon name="Search" size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No clients found</p>
              <p className="text-xs mt-1">Try searching by name or phone number</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;